import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Chart from "react-apexcharts";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import Label from "src/components/Label";
import { getTrackerDetailApi } from "src/DAL/tracker/tracker";
import {
  getInitials,
  getLeaveReasonLabel,
  getSubmissionPersonKey,
  getSubmissionReviewLabel,
  getSubmissionSourceLabel,
  isBmsMemberSource,
  loadSubmissionDetailFromApi,
  loadSubmissionsFromApi,
  markSubmissionSeenFromApi,
  sendSubmissionFeedbackApi,
  SUBMISSION_REVIEW,
  SUBMISSION_REVIEW_OPTIONS,
  updateSubmissionFromApi,
} from "./submissionStorage";
import { normalizeTracker } from "./trackerStorage";
import CoachReviewPanel from "./CoachReviewPanel";
import CoachFeedbackCard from "./CoachFeedbackCard";
import RemoveMemberCard from "./RemoveMemberCard";
import TagChipPicker from "./TagChipPicker";
import {
  buildSubmissionPerformance,
  formatDelta,
  formatMetricValue,
  formatPointLabel,
  parseMeasurableNumber,
} from "./submissionPerformance";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const Card = ({ title, description, action, children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: "12px",
      bgcolor: "#fff",
      mb: 2.5,
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      ...sx,
    }}
  >
    {(title || action) && (
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {title ? (
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
              {title}
            </Typography>
          ) : null}
          {description ? (
            <Typography sx={{ mt: 0.35, fontSize: 13, color: MUTED }}>
              {description}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
    )}
    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>{children}</Box>
  </Box>
);

const Fact = ({ label, value }) => (
  <Box
    sx={{
      p: 1.35,
      borderRadius: "8px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#f8fafc",
      height: "100%",
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        color: MUTED,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.5,
        fontSize: 13.5,
        fontWeight: 600,
        color: "#0f172a",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const reviewLabelColor = (status) => {
  if (status === SUBMISSION_REVIEW.OVERDUE) return "error";
  if (status === SUBMISSION_REVIEW.PENDING) return "warning";
  if (status === SUBMISSION_REVIEW.REVIEWED) return "success";
  return "default";
};

const trendMeta = (improved) => {
  if (improved === "up") {
    return {
      color: "#047857",
      bg: "rgba(16,185,129,0.12)",
      label: "Improving",
      Icon: TrendingUpIcon,
    };
  }
  if (improved === "down") {
    return {
      color: "#b91c1c",
      bg: "rgba(239,68,68,0.12)",
      label: "Declining",
      Icon: TrendingDownIcon,
    };
  }
  return {
    color: MUTED,
    bg: "#f1f5f9",
    label: "Stable",
    Icon: TrendingFlatIcon,
  };
};

const overallPerformanceMeta = (metrics = []) => {
  // Verdict from metrics that actually have a trend (2+ readings)
  const trendable = metrics.filter((m) => m.points?.length >= 2);
  const pool = trendable.length ? trendable : metrics;

  if (!pool.length) {
    return {
      label: "No measurable data yet",
      detail: "Add measurable questions to see progress over check-ins.",
      color: MUTED,
      bg: "#f8fafc",
      Icon: TrendingFlatIcon,
    };
  }
  const up = pool.filter((m) => m.improved === "up").length;
  const down = pool.filter((m) => m.improved === "down").length;
  const flat = pool.length - up - down;

  if (up > 0 && down === 0) {
    return {
      label: "On track",
      detail: `${up} metric${up === 1 ? "" : "s"} improving since baseline`,
      color: "#047857",
      bg: "rgba(16,185,129,0.12)",
      Icon: TrendingUpIcon,
      up,
      down,
      flat,
    };
  }
  if (down > 0 && up === 0) {
    return {
      label: "Needs attention",
      detail: `${down} metric${down === 1 ? "" : "s"} moving the wrong way`,
      color: "#b91c1c",
      bg: "rgba(239,68,68,0.12)",
      Icon: TrendingDownIcon,
      up,
      down,
      flat,
    };
  }
  if (up > 0 && down > 0) {
    return {
      label: "Mixed progress",
      detail: `${up} improving · ${down} declining`,
      color: "#b45309",
      bg: "rgba(245,158,11,0.14)",
      Icon: TrendingFlatIcon,
      up,
      down,
      flat,
    };
  }
  return {
    label: "Holding steady",
    detail: "No meaningful change vs baseline yet",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
    up,
    down,
    flat,
  };
};

const MetricChartCard = ({ metric }) => {
  const meta = trendMeta(metric.improved);
  const Icon = meta.Icon;
  const categories = metric.points.map(formatPointLabel);
  const data = metric.points.map((p) => p.value);
  const currentPoint = metric.points.find((p) => p.isCurrent);
  const midLabel = currentPoint ? "THIS CHECK-IN" : "LATEST";
  const midValue = currentPoint
    ? formatMetricValue(currentPoint.value, metric.unit)
    : formatMetricValue(metric.current, metric.unit);

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: "12px",
        border: `1px solid ${BORDER}`,
        bgcolor: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.25,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: "#0f172a" }}>
            {metric.label}
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12, color: MUTED }}>
            {metric.points.length} reading
            {metric.points.length === 1 ? "" : "s"}
            {metric.lowerBetter ? " · lower is better" : " · higher is better"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.4,
            borderRadius: "999px",
            bgcolor: meta.bg,
            color: meta.color,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
            {meta.label}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          mb: 1.25,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 800,
            color: "#0f172a",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          {formatMetricValue(metric.current, metric.unit)}
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: meta.color,
          }}
        >
          {formatDelta(metric.delta, metric.unit, metric.improved)}
          {metric.pctChange != null
            ? ` (${metric.pctChange > 0 ? "+" : ""}${metric.pctChange.toFixed(
                1
              )}%)`
            : ""}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 1,
          mb: 1.25,
          p: 1.1,
          borderRadius: "8px",
          bgcolor: "#f8fafc",
          border: `1px solid ${BORDER}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>
            BASELINE
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {formatMetricValue(metric.baseline, metric.unit)}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>
            {midLabel}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: PRIMARY }}>
            {midValue}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>
            CHANGE
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: meta.color }}>
            {formatDelta(metric.delta, metric.unit, metric.improved)}
          </Typography>
        </Box>
      </Box>

      {metric.points.length >= 2 ? (
        <Box sx={{ mt: "auto" }}>
          <Chart
            options={{
              chart: {
                type: "area",
                toolbar: { show: false },
                zoom: { enabled: false },
                fontFamily: "inherit",
                sparkline: { enabled: false },
              },
              colors: [meta.color === MUTED ? PRIMARY : meta.color],
              dataLabels: { enabled: false },
              stroke: { curve: "smooth", width: 2.5 },
              markers: {
                size: 4,
                hover: { size: 6 },
              },
              fill: {
                type: "gradient",
                gradient: {
                  shadeIntensity: 0.35,
                  opacityFrom: 0.35,
                  opacityTo: 0.05,
                  stops: [0, 90, 100],
                },
              },
              grid: { borderColor: "#f1f5f9", strokeDashArray: 3 },
              xaxis: {
                categories,
                labels: { style: { colors: MUTED, fontSize: "10px" } },
              },
              yaxis: {
                labels: {
                  style: { colors: MUTED, fontSize: "10px" },
                  formatter: (v) =>
                    Number.isFinite(v) ? String(Math.round(v * 10) / 10) : "",
                },
                forceNiceScale: true,
              },
              tooltip: {
                theme: "light",
                y: {
                  formatter: (v) => formatMetricValue(v, metric.unit),
                },
              },
            }}
            series={[{ name: metric.label, data }]}
            type="area"
            height={170}
          />
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.5 }}>
          Only one reading so far — more check-ins will draw the growth chart.
        </Typography>
      )}
    </Box>
  );
};

export default function TrackerSubmissionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [submission, setSubmission] = useState(null);
  const [journeyRows, setJourneyRows] = useState([]);
  const [tracker, setTracker] = useState(null);
  const [trackerStatuses, setTrackerStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMeta, setSavingMeta] = useState(false);
  const savingMetaRef = useRef(false);
  const openDropOut = searchParams.get("remove") === "1";

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setSubmission(null);
      setJourneyRows([]);
      setTracker(null);
      setTrackerStatuses([]);
      try {
        const result = await loadSubmissionDetailFromApi(id);
        if (cancelled) return;

        if (!result.ok || !result.submission) {
          enqueueSnackbar(result.message || "Submission not found", {
            variant: "error",
          });
          navigate("/tracker-submissions", { replace: true });
          return;
        }

        setSubmission(result.submission);

        if (result.submission.isNew) {
          markSubmissionSeenFromApi(result.submission.id).then((seen) => {
            if (!cancelled && seen.ok && seen.submission) {
              setSubmission(seen.submission);
            }
          });
        }

        if (result.submission.trackerId) {
          const [list, trackerRes] = await Promise.all([
            loadSubmissionsFromApi(result.submission.trackerId, {
              limit: 500,
            }),
            getTrackerDetailApi(result.submission.trackerId).catch(() => null),
          ]);
          if (!cancelled) {
            setJourneyRows(list.rows || []);
            if (trackerRes?.code === 200 && trackerRes.tracker) {
              const shaped = normalizeTracker(trackerRes.tracker);
              setTracker(shaped);
              setTrackerStatuses(
                Array.isArray(shaped?.statuses) ? shaped.statuses : []
              );
            }
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, enqueueSnackbar, navigate]);

  const handleMetaUpdate = async (patch) => {
    if (!submission?.id || savingMetaRef.current) return;
    if (patch?.error) {
      enqueueSnackbar(patch.error, { variant: "warning" });
      return;
    }
    const { memberFeedback, ...metaPatch } = patch;
    savingMetaRef.current = true;
    setSavingMeta(true);
    const result = await updateSubmissionFromApi(submission.id, metaPatch);
    if (!result.ok) {
      savingMetaRef.current = false;
      setSavingMeta(false);
      enqueueSnackbar(result.message || "Could not update", {
        variant: "error",
      });
      return;
    }
    let nextSubmission = result.submission;
    let notifyIncomplete = false;
    if (
      memberFeedback?.message &&
      (memberFeedback.sendChat || memberFeedback.sendEmail)
    ) {
      const sent = await sendSubmissionFeedbackApi(submission.id, {
        message: memberFeedback.message,
        sendChat: Boolean(memberFeedback.sendChat),
        sendEmail: Boolean(memberFeedback.sendEmail),
      });
      if (!sent.ok) {
        savingMetaRef.current = false;
        setSavingMeta(false);
        setSubmission(nextSubmission);
        enqueueSnackbar(
          sent.message || "Review saved, but feedback could not be sent",
          { variant: "warning" }
        );
        return;
      }
      if (sent.submission) nextSubmission = sent.submission;
      if (sent.results?.chat?.sent) {
        enqueueSnackbar("Chat message sent", { variant: "success" });
      } else if (memberFeedback.sendChat && sent.results?.chat) {
        notifyIncomplete = true;
        enqueueSnackbar(
          sent.results.chat.error || "Chat message not sent",
          { variant: "warning" }
        );
      }
      if (sent.results?.email?.sent) {
        enqueueSnackbar("Email sent", { variant: "success" });
      } else if (memberFeedback.sendEmail && sent.results?.email) {
        notifyIncomplete = true;
        enqueueSnackbar(
          sent.results.email.error || "Email not sent",
          { variant: "warning" }
        );
      }
    }
    savingMetaRef.current = false;
    setSavingMeta(false);
    setSubmission(nextSubmission);
    const dropped =
      metaPatch.statusName != null &&
      /drop/i.test(String(metaPatch.statusName));
    if (!notifyIncomplete) {
      enqueueSnackbar(
        dropped
          ? "Member dropped out of the journey"
          : metaPatch.reviewStatus === "reviewed"
            ? "Review completed"
            : "Submission updated",
        { variant: "success" }
      );
    } else if (metaPatch.reviewStatus === "reviewed") {
      enqueueSnackbar("Review saved (some messages did not send)", {
        variant: "warning",
      });
    }
    if (dropped && nextSubmission) {
      const list = await loadSubmissionsFromApi(nextSubmission.trackerId, {
        limit: 500,
      });
      if (list?.ok) setJourneyRows(list.rows || []);
    }
  };

  const statusOptions = useMemo(() => {
    const names = new Set();
    (trackerStatuses || []).forEach((s) => {
      const name = s?.name || s?.label;
      if (name) names.add(String(name));
    });
    if (submission?.statusName) names.add(submission.statusName);
    // Always offer Dropped Out so coaches can drop via status even if the
    // journey vocabulary forgot to include it.
    if (![...names].some((n) => /drop/i.test(n))) {
      names.add("Dropped Out");
    }
    return Array.from(names);
  }, [trackerStatuses, submission?.statusName]);

  const trackerTagOptions = useMemo(() => {
    const names = new Set();
    (Array.isArray(tracker?.tags) ? tracker.tags : []).forEach((t) => {
      if (t) names.add(String(t));
    });
    (Array.isArray(submission?.tags) ? submission.tags : []).forEach((t) => {
      if (t) names.add(String(t));
    });
    return Array.from(names);
  }, [tracker?.tags, submission?.tags]);

  const performance = useMemo(
    () =>
      submission
        ? buildSubmissionPerformance(submission, journeyRows, tracker)
        : null,
    [submission, journeyRows, tracker]
  );

  const journeyHistory = useMemo(() => {
    if (!submission?.trackerId) return [];
    const personKey = getSubmissionPersonKey(submission);
    return (journeyRows || [])
      .filter((row) => getSubmissionPersonKey(row) === personKey)
      .sort(
        (a, b) =>
          new Date(a.submittedAt || 0) - new Date(b.submittedAt || 0)
      );
  }, [submission, journeyRows]);

  // Member left, or coach/admin removed / Dropped Out. Reactivation uses
  // statusBeforeLeave on the stop row.
  const leaveInfo = useMemo(() => {
    if (!submission) return null;
    const isLeftName = (name) => String(name || "").toLowerCase() === "left";
    const isDropName = (name) => /drop/i.test(String(name || ""));
    const isStopRow = (row) =>
      Boolean(row?.leftAt) ||
      isLeftName(row?.statusName) ||
      isDropName(row?.statusName);
    if (!isStopRow(submission)) return null;
    const leftRow =
      [...journeyHistory].reverse().find((r) => isStopRow(r)) || submission;
    const defaultStatus =
      (trackerStatuses || []).find((s) => s?.assignedOnSubmit) ||
      (trackerStatuses || [])[0] ||
      null;
    const leftAt = leftRow.leftAt || submission.leftAt;
    const leftBy = leftRow.leftBy || submission.leftBy || "";
    const staffRemoved = leftBy === "coach" || leftBy === "admin";
    const freeReason = String(
      leftRow.leftReason || submission.leftReason || ""
    ).trim();
    return {
      rowId: leftRow.id,
      statusName:
        leftRow.statusBeforeLeave ||
        defaultStatus?.name ||
        defaultStatus?.label ||
        "Submitted",
      title: staffRemoved
        ? "Removed from journey"
        : isDropName(leftRow.statusName || submission.statusName)
          ? "Dropped out"
          : "Left the journey",
      reasonLabel:
        getLeaveReasonLabel(freeReason) || (staffRemoved ? freeReason : ""),
      note: leftRow.leftNote || submission.leftNote || "",
      dateLabel: leftAt ? moment(leftAt).format("DD MMM YYYY") : "",
    };
  }, [submission, journeyHistory, trackerStatuses]);

  const handleReactivate = async () => {
    if (!leaveInfo || !submission?.id) return;
    const { rowId, statusName } = leaveInfo;
    if (rowId === submission.id) {
      await handleMetaUpdate({ statusName });
      return;
    }
    if (savingMeta) return;
    setSavingMeta(true);
    const result = await updateSubmissionFromApi(rowId, { statusName });
    const refreshed = result.ok
      ? await loadSubmissionDetailFromApi(submission.id)
      : null;
    setSavingMeta(false);
    if (!result.ok) {
      enqueueSnackbar(result.message || "Could not reactivate", {
        variant: "error",
      });
      return;
    }
    setJourneyRows((rows) =>
      rows.map((r) => (r.id === result.submission.id ? result.submission : r))
    );
    if (refreshed?.ok && refreshed.submission) {
      setSubmission(refreshed.submission);
    }
    enqueueSnackbar("Member reactivated", { variant: "success" });
  };

  // Merge only the feedback fields so an in-progress coach review isn't reset.
  const handleFeedbackSent = async (updated) => {
    if (!submission?.id) return;
    let fresh = updated;
    if (!fresh) {
      const refreshed = await loadSubmissionDetailFromApi(submission.id);
      fresh = refreshed.ok ? refreshed.submission : null;
    }
    if (!fresh) return;
    setSubmission((prev) =>
      prev && prev.id === fresh.id
        ? {
            ...prev,
            coachFeedback: fresh.coachFeedback,
            coachNotes: fresh.coachNotes,
          }
        : prev
    );
  };

  const handleMemberRemoved = async (updated) => {
    if (!submission?.id) return;
    let fresh = updated;
    if (!fresh) {
      const refreshed = await loadSubmissionDetailFromApi(submission.id);
      fresh = refreshed.ok ? refreshed.submission : null;
    }
    if (fresh) setSubmission(fresh);
    const list = await loadSubmissionsFromApi(submission.trackerId, {
      limit: 500,
    });
    if (list?.ok) setJourneyRows(list.rows || []);
  };

  const answerGroups = useMemo(() => {
    if (!submission) return { measurable: [], other: [] };
    const metricLabels = new Set(
      (performance?.metrics || []).map((m) =>
        String(m.label || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, " ")
          .trim()
      )
    );
    const measurable = [];
    const other = [];
    Object.entries(submission.answers || {}).forEach(([key, value]) => {
      const norm = String(key)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
        .replace(/^(starting|current)\s+/, "");
      const isMetric =
        parseMeasurableNumber(value) != null &&
        (metricLabels.has(norm) ||
          /weight|body\s*fat|bmi|energy|sessions|score|height|adherence/i.test(
            key
          ));
      if (isMetric) measurable.push([key, value]);
      else other.push([key, value]);
    });
    return { measurable, other };
  }, [submission, performance]);

  if (loading || !submission) {
    return (
      <div className="container-fluid tracker-module-page">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "55vh",
            width: "100%",
          }}
        >
          <CircularProgress size={40} color="primary" />
        </Box>
      </div>
    );
  }

  const metrics = performance?.metrics || [];
  const displayMetrics = metrics.filter(
    (m) =>
      m.points.length >= 2 ||
      m.points.some((p) => p.isCurrent) ||
      m.improved !== "flat"
  );
  const overall = overallPerformanceMeta(metrics);
  const OverallIcon = overall.Icon;
  const backToList = submission.trackerId
    ? `/tracker-submissions?tracker=${encodeURIComponent(submission.trackerId)}`
    : "/tracker-submissions";

  return (
    <div className="container-fluid tracker-module-page">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            <IconButton
              size="small"
              onClick={() => navigate(backToList)}
              sx={{ p: 0.75 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography
              component="button"
              type="button"
              onClick={() => navigate(backToList)}
              sx={{
                border: 0,
                background: "none",
                p: 0,
                cursor: "pointer",
                color: MUTED,
                fontSize: 13,
                "&:hover": { color: PRIMARY },
              }}
            >
              Submissions
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: 13 }}>/</Typography>
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: 13,
                fontWeight: 600,
                maxWidth: 280,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {submission.memberName}
            </Typography>
            <Label
              variant="ghost"
              color={
                isBmsMemberSource(submission.source)
                  ? "warning"
                  : "info"
              }
            >
              {getSubmissionSourceLabel(submission.source)}
            </Label>
            <Label
              variant="ghost"
              color={reviewLabelColor(submission.reviewStatus)}
            >
              {getSubmissionReviewLabel(submission.reviewStatus)}
            </Label>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                fontSize: 15,
                fontWeight: 700,
                bgcolor: PRIMARY_SOFT,
                color: PRIMARY,
              }}
            >
              {getInitials(submission.memberName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 22, md: 26 },
                  color: "#0f172a",
                  lineHeight: 1.2,
                }}
              >
                {submission.memberName}
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 13.5, color: MUTED }}>
                {submission.formName || "Form"}
                {submission.trackerName ? ` · ${submission.trackerName}` : ""}
                {submission.submittedAt
                  ? ` · ${moment(submission.submittedAt).format(
                      "DD MMM YYYY HH:mm"
                    )}`
                  : ""}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            select
            size="small"
            label="Status"
            value={submission.statusName || ""}
            disabled={savingMeta}
            onChange={(e) => handleMetaUpdate({ statusName: e.target.value })}
            sx={{ minWidth: 160, bgcolor: "#fff" }}
          >
            {statusOptions.length ? (
              statusOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={submission.statusName || "Submitted"}>
                {submission.statusName || "Submitted"}
              </MenuItem>
            )}
          </TextField>
          <TextField
            select
            size="small"
            label="Review"
            value={submission.reviewStatus || SUBMISSION_REVIEW.NONE}
            disabled={savingMeta}
            onChange={(e) => handleMetaUpdate({ reviewStatus: e.target.value })}
            sx={{ minWidth: 150, bgcolor: "#fff" }}
          >
            {(SUBMISSION_REVIEW_OPTIONS.length
              ? SUBMISSION_REVIEW_OPTIONS.filter((o) => o.value !== "all")
              : [
                  { value: "none", label: "None" },
                  { value: "pending", label: "Pending" },
                  { value: "overdue", label: "Overdue" },
                  { value: "reviewed", label: "Reviewed" },
                ]
            ).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          {submission.trackerId ? (
            <button
              type="button"
              className="small-contained-outlined-button"
              onClick={() => navigate(`/trackers/${submission.trackerId}`)}
            >
              Open tracker
            </button>
          ) : null}
          <button
            type="button"
            className="small-contained-button"
            onClick={() => navigate(backToList)}
          >
            All submissions
          </button>
        </Box>
      </Box>

      <Box
        sx={{
          mb: 2,
          p: 1.75,
          borderRadius: "12px",
          border: `1px solid ${BORDER}`,
          bgcolor: "#fff",
        }}
      >
        <TagChipPicker
          label="Tags"
          options={trackerTagOptions}
          value={submission.tags || []}
          disabled={savingMeta}
          onChange={(tags) => handleMetaUpdate({ tags })}
          emptyLabel="Add tags on the journey first (Tracker → Tags)."
        />
      </Box>

      {leaveInfo ? (
        <Alert
          severity="info"
          sx={{ mb: 2, alignItems: "center", borderRadius: "12px" }}
          action={
            <button
              type="button"
              className="small-contained-button"
              disabled={savingMeta}
              onClick={handleReactivate}
            >
              Reactivate
            </button>
          }
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
            {[leaveInfo.title, leaveInfo.reasonLabel, leaveInfo.dateLabel]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          {leaveInfo.note ? (
            <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
              “{leaveInfo.note}”
            </Typography>
          ) : null}
        </Alert>
      ) : (
        <RemoveMemberCard
          submission={submission}
          onRemoved={handleMemberRemoved}
          defaultOpen={openDropOut}
        />
      )}

      <CoachReviewPanel
        tracker={tracker}
        submission={submission}
        saving={savingMeta}
        onSubmit={handleMetaUpdate}
      />

      <CoachFeedbackCard
        submission={submission}
        metrics={metrics}
        onSent={handleFeedbackSent}
      />

      {/* Performance snapshot — first thing coaches see */}
      <Box
        sx={{
          mb: 2.5,
          p: { xs: 2, sm: 2.25 },
          borderRadius: "12px",
          border: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(220px, 1.1fr) repeat(3, minmax(0, 1fr))",
          },
          gap: 1.5,
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pr: { md: 1.5 },
            borderRight: { md: `1px solid ${BORDER}` },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: overall.bg,
              color: overall.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <OverallIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.45,
                color: MUTED,
                textTransform: "uppercase",
              }}
            >
              Performance
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 20,
                color: overall.color,
                lineHeight: 1.2,
              }}
            >
              {overall.label}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 12.5, color: MUTED }}>
              {overall.detail}
            </Typography>
          </Box>
        </Box>

        {[
          {
            label: "Check-ins",
            value: String(performance?.historyCount || journeyHistory.length || 1),
            hint: "for this person",
          },
          {
            label: "Improving",
            value: String(overall.up ?? 0),
            hint: "metrics vs baseline",
            color: "#047857",
          },
          {
            label: "Needs work",
            value: String(overall.down ?? 0),
            hint: "metrics vs baseline",
            color: overall.down ? "#b91c1c" : MUTED,
          },
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{
              px: { md: 1 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                color: MUTED,
                textTransform: "uppercase",
              }}
            >
              {stat.label}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                fontSize: 26,
                fontWeight: 800,
                color: stat.color || "#0f172a",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              {stat.value}
            </Typography>
            <Typography sx={{ mt: 0.2, fontSize: 12, color: MUTED }}>
              {stat.hint}
            </Typography>
          </Box>
        ))}
      </Box>

      {displayMetrics.length > 0 ? (
        <Card
          title="Measurable progress"
          description="Baseline → this check-in, across every reading for this person on the journey."
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: displayMetrics.length === 1 ? "1fr" : "1fr 1fr",
              },
              gap: 1.5,
            }}
          >
            {displayMetrics.map((metric) => (
              <MetricChartCard key={metric.id} metric={metric} />
            ))}
          </Box>
        </Card>
      ) : (
        <Card
          title="Measurable progress"
          description="No measurable answers on this journey yet."
        >
          <Typography sx={{ fontSize: 13.5, color: MUTED }}>
            Mark questions as measurable (weight, scores, sessions) to see
            baseline → current trends here.
          </Typography>
        </Card>
      )}

      <div>
          {answerGroups.measurable.length > 0 ? (
            <Card
              title="Numbers on this check-in"
              description="Measurable answers from this submission only."
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "1fr 1fr 1fr",
                  },
                  gap: 1.25,
                }}
              >
                {answerGroups.measurable.map(([key, value]) => (
                  <Box
                    key={key}
                    sx={{
                      p: 1.5,
                      borderRadius: "10px",
                      border: `1px solid ${BORDER}`,
                      bgcolor: PRIMARY_SOFT,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: MUTED,
                        textTransform: "uppercase",
                        letterSpacing: 0.35,
                      }}
                    >
                      {key}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 20,
                        fontWeight: 800,
                        color: "#0f172a",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {String(value || "—")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          ) : null}

          <Card
            title="Notes & answers"
            description="Everything else submitted on this form."
          >
            {answerGroups.other.length === 0 &&
            answerGroups.measurable.length === 0 ? (
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>
                No answers stored on this submission.
              </Typography>
            ) : answerGroups.other.length === 0 ? (
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>
                Only measurable numbers on this check-in — see above.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                  gap: 1.25,
                }}
              >
                {answerGroups.other.map(([key, value]) => (
                  <Fact key={key} label={key} value={String(value || "—")} />
                ))}
              </Box>
            )}
          </Card>

          {journeyHistory.length > 1 ? (
            <Card
              title="Journey check-ins"
              description="Same person on this tracker — click to compare."
            >
              <Box sx={{ display: "grid", gap: 1 }}>
                {journeyHistory.map((row, index) => {
                  const active = row.id === submission.id;
                  return (
                    <Box
                      key={row.id}
                      component="button"
                      type="button"
                      onClick={() => {
                        if (!active) navigate(`/tracker-submissions/${row.id}`);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        textAlign: "left",
                        width: "100%",
                        border: `1px solid ${active ? PRIMARY : BORDER}`,
                        bgcolor: active ? PRIMARY_SOFT : "#fff",
                        borderRadius: "10px",
                        px: 1.5,
                        py: 1.15,
                        cursor: active ? "default" : "pointer",
                        transition: "border-color 0.15s, background 0.15s",
                        "&:hover": active
                          ? undefined
                          : { borderColor: PRIMARY, bgcolor: "#f8fafc" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "999px",
                          bgcolor: active ? PRIMARY : "#e2e8f0",
                          color: active ? "#fff" : "#334155",
                          fontSize: 12,
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: "#0f172a",
                          }}
                        >
                          {row.formName || "Submission"}
                          {active ? " · viewing" : ""}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: MUTED,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.answerSummary ||
                            (row.submittedAt
                              ? moment(row.submittedAt).format("DD MMM YYYY")
                              : "—")}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: MUTED,
                          flexShrink: 0,
                        }}
                      >
                        {row.submittedAt
                          ? moment(row.submittedAt).format("DD MMM")
                          : ""}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Card>
          ) : null}
      </div>
    </div>
  );
}
